// External libraries
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { flowRight, find } from 'lodash';
import { connect } from 'react-redux';
import { withCustomMetrics } from '@zocdoc/frontend-metrics';
import { saveProfessional } from '../../components/DoctorCard/DoctorCardActions';

// Zocdoc dependencies
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { slate70 } from '@zocdoc-mezzanine/foundations-colors';
import VirtualVisitIcon from '@zocdoc-frontend-common/assets-icons/VirtualVisitIcon';
import { MetricsClickable } from '@zocdoc/frontend-metrics';

// This package
import DoctorLocation from '@zocdoc-frontend-common/components-lightweight-doctor-card/DoctorLocation';
import DoctorPhoto from '@zocdoc-frontend-common/components-lightweight-doctor-card/DoctorPhoto';

import {
    SCHEDULING_ROOT_KEY,
    DOCTOR_CARD_ROOT_KEY,
    TRACKING_CONSTANTS,
} from '../../constants';
import { withProvider } from '../../../gql';

import patientSelector from '../../selectors/PatientSelector';

/* "Lightweight" Doctor Card (not for search!) that links to a doctor's profile
 * for profile, booking, etc.
 */
export class DoctorCardHeader extends PureComponent {
    static propTypes = {
        className: PropTypes.string,
        provider: PropTypes.shape({
            averageRating: PropTypes.number,
            frontEndCirclePictureUrl: PropTypes.string,
            distance: PropTypes.number,
            firstName: PropTypes.string,
            id: PropTypes.string,
            index: PropTypes.number,
            lastName: PropTypes.string,
            title: PropTypes.string,
            prenominal: PropTypes.string,
            profileUrl: PropTypes.string,
            relevantSpecialty: PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
            }),
            reviewCount: PropTypes.number,
            optOutOfRatings: PropTypes.bool,
        }),
        location: PropTypes.shape({
            address1: PropTypes.string,
            address2: PropTypes.string,
            address3: PropTypes.string,
            isVirtual: PropTypes.bool,
            state: PropTypes.string,
            city: PropTypes.string,
            zipCode: PropTypes.string,
        }),
        index: PropTypes.number,
        ref: PropTypes.object,
        sendMetric: PropTypes.func,
        isMobileOrTablet: PropTypes.bool,
        showOldBookingPage: PropTypes.bool,
    };

    static defaultProps = {
        index: null,
    };

    getProviderNameTitle() {
        const { provider } = this.props;
        return `${provider.prenominal} ${provider.firstName} ${provider.lastName}, ${provider.title}`;
    }

    componentWillReceiveProps(nextProps) {
        const { professional } = nextProps;
        const { saveProfessional, hasProfessional } = this.props;

        if (professional && !hasProfessional) {
            saveProfessional(professional);
        }
    }

    render() {
        const {
            className,
            formattedAppointmentTime,
            hasErrors,
            provider,
            providerLoading,
            location,
            ref,
            showOldBookingPage,
        } = this.props;

        if (hasErrors || providerLoading || !provider || !location) {
            return null;
        }

        const { isVirtual } = location;

        return (
            <MetricsClickable
                action="Click LightweightDoctorCard"
                className={className}
            >
                <LightweightDoctorCardContainer
                    ref={ref}
                    data-test="lightweight-doctor-card-container"
                    target="_blank"
                    showOldBookingPage={showOldBookingPage}
                >
                    <DoctorInfoTopSection>
                        <DoctorImageWrapper>
                            <StyledDoctorPhoto
                                data-test="lightweight-doctor-card-image"
                                provider={provider}
                                size="100%"
                            />
                        </DoctorImageWrapper>
                        <DoctorInfo>
                            <Name
                                data-test="doctor-card-header-doctor-name"
                                itemProp="name"
                            >
                                {this.getProviderNameTitle()}
                            </Name>
                            <AppointmentTime
                                data-test="appointment-time-wrapper"
                                data-uem-id="appointment-time"
                            >
                                {formattedAppointmentTime}
                            </AppointmentTime>
                            {isVirtual ? (
                                <VirtualVisit>
                                    <StyledVirtualVisitIcon skipCircle />
                                    Video visit
                                </VirtualVisit>
                            ) : (
                                <StyledDoctorLocation location={location} />
                            )}
                        </DoctorInfo>
                    </DoctorInfoTopSection>
                </LightweightDoctorCardContainer>
            </MetricsClickable>
        );
    }
}

const LightweightDoctorCardContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    box-sizing: border-box;

    ${p =>
        p.showOldBookingPage &&
        css`
            max-width: 552px;
            float: none;
            margin: auto;
            padding-left: 22px;
        `} {
    }
`;

const DoctorInfoTopSection = styled.div`
    display: flex;
    margin-bottom: 12px;
    margin-top: 12px;
`;

const DoctorImageWrapper = styled.div`
    width: 64px;
    height: 64px;
    position: relative;
    margin-bottom: 4px;
    ${breakpoint.small} {
        margin-bottom: 10px;
    }
`;

const StyledDoctorPhoto = styled(DoctorPhoto)`
    position: relative;
    ${breakpoint.small} {
        max-height: 150px;
        max-width: 150px;
    }
`;

const DoctorInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-left: 8px;
`;

const AppointmentTime = styled.span`
    font: 14px/20px ${FontFamily.SharpSansMedium};
`;

const VirtualVisit = styled.span`
    font: 14px/20px ${FontFamily.SharpSansMedium};
    color: ${slate70};
    margin-top: -2px;
`;

const Name = styled.span`
    box-sizing: border-box;
    display: block;
    font: 16px/24px ${FontFamily.SharpSansBold};
`;

const StyledVirtualVisitIcon = styled(VirtualVisitIcon)`
    color: ${slate70};
    vertical-align: middle;
    margin-bottom: 1px;
    margin-left: -4px;
`;

const StyledDoctorLocation = styled(DoctorLocation)`
    font: 14px/20px ${FontFamily.SharpSansMedium};
    color: ${slate70};
    margin-top: 0px;
`;

const getSelectedPatientName = (state, patientId) => {
    const patient = patientSelector(state);
    if (patient && patientId) {
        const patientIdAsInt = parseInt(patientId, 10);
        if (patient.patientFirstName && patient.patientId === patientIdAsInt) {
            return `${patient.patientFirstName} ${patient.patientLastName}`;
        } else {
            const subPatient = find(
                patient.subpatients,
                x => x.patientId === patientIdAsInt
            );
            if (subPatient) {
                return `${subPatient.firstName} ${subPatient.lastName}`;
            }
        }
    }
    return undefined;
};

export const mapStateToProps = state => {
    const { bookingState } = state[SCHEDULING_ROOT_KEY];
    const { hasProfessional } = state[DOCTOR_CARD_ROOT_KEY];
    const patientId = bookingState ? bookingState.patient_id : undefined;
    const patientName = getSelectedPatientName(state, patientId);
    const { metrics = {} } = state.ssr || {};

    return {
        hasProfessional,
        patientName,
        pageName: metrics.page || '',
    };
};

export const mapDispatchToProps = {
    saveProfessional,
};

export default flowRight(
    withCustomMetrics({ component: TRACKING_CONSTANTS.ProviderCard }),
    connect(mapStateToProps, mapDispatchToProps),
    withProvider
)(DoctorCardHeader);
